import { useEffect, useRef, useState, useCallback } from 'react';
import useHexToRgb from '../../../AsianExchangeSrc/utils/useHexToRgb';
import { useSelector } from 'react-redux';
import ProviderTabs from './ProviderTabs';
import CategoryTabs from './CategoryTabs';
import GameGrid from './GameGrid';
import { useSearchParams } from 'react-router-dom';

const INITIAL_PROVIDER_LIST = [{ provider: { name: 'All' } }];

// 🔹 Hook to scroll a tab item into center
const useScrollToCenter = () => {
    const scrollRef = useRef(null);
    const itemRefs = useRef([]);

    const scrollToCenter = useCallback((index) => {
        const container = scrollRef.current;
        const item = itemRefs.current[index];
        if (container && item) {
            const scrollLeft = item.offsetLeft - container.offsetWidth / 2 + item.offsetWidth / 2;
            container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        }
    }, []);

    return { scrollRef, itemRefs, scrollToCenter };
};

// 🔹 Hook to extract unique categories from one or multiple providers
const useFilteredCategories = (providerOrList, filterSet = new Set(['CASINO'])) => {
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const categorySet = new Set(filterSet);

        const providers = Array.isArray(providerOrList)
            ? providerOrList.map(p => p?.provider).filter(Boolean)
            : [providerOrList];

        providers.forEach((provider) => {
            const gameList = provider?.gameList || [];
            gameList.forEach(({ category }) => {
                category?.split('/')?.forEach((cat) => {
                    if (cat && cat !== 'CASINO') categorySet.add(cat);
                });
            });
        });

        setCategories([...categorySet]);
    }, [providerOrList]);

    return categories;
};

function CasinoHomeNav({ providerList = [], headerColor = { start: '#315195', end: '#14213D' } }) {
    const [searchParams] = useSearchParams();
    const qLiveCasino = searchParams.get("live-casino");

    const q1 = searchParams.get("q1");
    const q2 = searchParams.get("q2");

    const topBarColor = useSelector((state) => state?.site?.siteDetails?.topBarColor);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [activeCategoryTabIndex, setActiveCategoryTabIndex] = useState(0);

    const finalProviderList = [...INITIAL_PROVIDER_LIST, ...providerList];
    const activeProvider = finalProviderList[activeTabIndex]?.provider;

    const categories = useFilteredCategories(
        activeTabIndex === 0 ? providerList : activeProvider
    );

    const { scrollRef: providerScrollRef, itemRefs: providerItemRefs, scrollToCenter: scrollToProvider } = useScrollToCenter();
    const { scrollRef: categoryScrollRef, itemRefs: categoryItemRefs, scrollToCenter: scrollToCategory } = useScrollToCenter();

    const rgbColorStart = useHexToRgb(topBarColor?.start);
    const rgbColorEnd = useHexToRgb(topBarColor?.end);

    const rgbBorderColorStart = useHexToRgb(headerColor?.start);
    const rgbBorderColorEnd = useHexToRgb(headerColor?.end);

    const handleProviderClick = (index, e) => {
        e.preventDefault();
        setActiveTabIndex(index);
        setActiveCategoryTabIndex(0);
        scrollToProvider(index);
    };

    const handleCategoryClick = (index, e) => {
        e.preventDefault();
        setActiveCategoryTabIndex(index);
        scrollToCategory(index);
    };

    useEffect(() => {
      if(qLiveCasino==="true" && q1 && q2){
        setActiveTabIndex(0);
        const categoryIndex = categories.findIndex(c=> c.replaceAll("_", " ").toLowerCase().includes(q2));
        if(categoryIndex !== -1){
            setActiveCategoryTabIndex(categoryIndex);
        }
      }else{
        setActiveTabIndex(0);
        setActiveCategoryTabIndex(0);
      }
    
    }, [q1, q2])
    

    return (
        <div className={`w-full flex flex-col sticky top-14 justify-start items-start z-1`}
            style={{
                top: window.location.pathname.includes("/live-casino") ? '112px' : '500px',
                // zIndex: 999, // ensure it sticks above other elements
              }}
            >

            <ProviderTabs
                providers={finalProviderList}
                activeIndex={activeTabIndex}
                onClick={handleProviderClick}
                scrollRef={providerScrollRef}
                itemRefs={providerItemRefs}
                headerColor={headerColor}
                topBarColor={topBarColor}
            />

            {categories.length > 0 && (
                <CategoryTabs
                    categories={categories}
                    activeIndex={activeCategoryTabIndex}
                    onClick={handleCategoryClick}
                    scrollRef={categoryScrollRef}
                    itemRefs={categoryItemRefs}
                    topBarColor={topBarColor}
                />
            )}

            <GameGrid
                gameList={
                    activeTabIndex === 0
                        ? providerList.flatMap(p => p.provider?.gameList || [])
                        : activeProvider?.gameList || []
                }
                categoryFilter={categories[activeCategoryTabIndex]}
                rgbColorStart={rgbColorStart}
                rgbColorEnd={rgbColorEnd}
                borderColorStart={rgbBorderColorStart}
                borderColorEnd={headerColor?.end}
            />
        </div>
    );
}

export default CasinoHomeNav;


